import { type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Select } from "../../../components/ui/Select";
import type { Api } from "../../../services/api";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import {
  pickerProfessions,
  type ProfessionListState,
} from "../components/professionPriority";

type TranslatableItem = {
  uid: string;
  translations: Record<string, string | null>;
};

type ProfessionItem = Pick<
  Api["get"]["/webapp/public/professions"]["output"]["body"][number],
  "uid" | "translations" | "onboardingVisibility"
>;

/**
 * The profession and the specialty, on one screen, as two dropdowns.
 *
 * The mobile carousel spends a slide on each because each one is a long list
 * and a slide is all the room a phone has. The desktop form asks them together
 * (the same grouping the name and the country get), so both collapse into the
 * DS `Select`: the search moves inside the panel, and the second dropdown
 * re-scopes itself to whatever the first one answered.
 *
 * The specialty dropdown is absent when there is nothing to ask — a profession
 * with exactly one specialty, or one the RPPS already named. `hasSpecialtyStep`
 * is the flow's own word for that, and it is the same word the Continue's
 * validity is gated on, so the two cannot disagree about whether the question
 * is on screen.
 *
 * The profession dropdown lists what the mobile picker lists, and its "Show
 * more" row, pinned at the bottom of the panel, adds the longer list without
 * closing it. Unlike the mobile button, the row stays during a search: opening
 * the panel pre-fills the search with the selected label, and the row must
 * still be reachable then.
 *
 * Purely presentational: no state, no effects, no refs.
 */
export function OnboardingRoleFields({
  professions,
  selectedProfession,
  onSelectProfession,
  specialties,
  selectedSpecialty,
  onSelectSpecialty,
  hasSpecialtyStep,
  onSubmit,
  professionList,
  onShowMore,
}: {
  professions: ProfessionItem[];
  selectedProfession: string | null;
  onSelectProfession: (uid: string) => void;
  /** Already scoped to the selected profession by the flow. */
  specialties: TranslatableItem[];
  selectedSpecialty: string;
  onSelectSpecialty: (uid: string) => void;
  /** False when the specialty is settled and must not be asked. */
  hasSpecialtyStep: boolean;
  onSubmit: () => void;
  professionList: ProfessionListState;
  onShowMore: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();

  const { listed, all, offersMore } = pickerProfessions({
    professions,
    selected: selectedProfession,
    list: professionList,
  });
  const toOption = (profession: ProfessionItem) => ({
    value: profession.uid,
    label: getItemTranslation(profession),
  });
  const professionOptions = listed.map(toOption);
  // A typed query also finds the professions "Show more" would add.
  const unlisted = all
    .filter((profession) => !listed.includes(profession))
    .map(toOption);

  // Alphabetical in the language being read. The endpoint orders by `uid`,
  // which is alphabetical among ENGLISH slugs and therefore nothing at all in
  // the other seven — the same sort the mobile slide does, for the same reason.
  const specialtyOptions = specialties
    .map((specialty) => ({
      value: specialty.uid,
      label: getItemTranslation(specialty),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <form
      id="onboarding-form-profession"
      className="flex w-full flex-col gap-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="flex w-full flex-col gap-1">
        <label className="font-dm-sans text-ui-note text-ds-black">
          {t("Role")}
        </label>
        <Select
          appearance="field"
          value={selectedProfession === null ? "" : selectedProfession}
          onChange={(option) => {
            onSelectProfession(option.value);
          }}
          options={professionOptions}
          searchOnlyOptions={unlisted}
          placeholder={t("Select your role")}
          searchable
          variant="ghost"
          className="rounded-xl border-ds-slate-200 bg-white"
          footerAction={
            offersMore
              ? {
                  label: t("Show more professions"),
                  // A typed search would hide the professions the action
                  // adds: the panel stays open and the search is cleared.
                  panel: "stay-open",
                  onSelect: onShowMore,
                }
              : null
          }
        />
      </div>

      {hasSpecialtyStep && (
        <div className="flex w-full flex-col gap-1">
          <label className="font-dm-sans text-ui-note text-ds-black">
            {t("Main specialty")}
          </label>
          <Select
            appearance="field"
            footerAction={null}
            searchOnlyOptions={null}
            value={selectedSpecialty}
            onChange={(option) => {
              onSelectSpecialty(option.value);
            }}
            options={specialtyOptions}
            placeholder={t("Select your specialty")}
            searchable
            variant="ghost"
            className="rounded-xl border-ds-slate-200 bg-white"
          />
        </div>
      )}
    </form>
  );
}
